




















































































import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {VueMaskDirective} from 'v-mask';
import BindAttrs from '@/utils/binds-attrs';
import AmetrasSimpleTextOutputTemplate from '@/fields/AmetrasSimpleTextOutputTemplate.vue';
import {isEqual} from 'lodash';
import '@/compiled-icons/chevron-right';
import '@/compiled-icons/chevron-left';
import {ICONS_SERVICE_INSTANCE} from '@/utils/icons.service';
import {FieldStyleContext} from '@/fields/field-style-context';

/**
 * General Component for Fields of type DATE
 *
 * https://confluence.ametras.com/x/L4RQAw
 *
 * attachRelatively:
 *  - true: attaches the dialog relatively to the component wrapper
 *          This is needed in case of a position:fixed surrounding panel (usually NOT)
 */
@Component({
  components: {AmetrasSimpleTextOutputTemplate},
  directives: {
    VueMaskDirective
  },
  mixins: [BindAttrs]
})
export default class AmetrasHexColorPickerTemplate extends Vue {
  @Prop() textAlign!: 'LEFT' | 'RIGHT' | 'CENTER' | 'JUSTIFIED';
  @Prop({
    required: false
  }) value!: string;
  @Prop({
    required: false,
    default: false
  }) required!: boolean;
  @Prop() marginDefinition!: string;
  @Prop() paddingDefinition!: string;
  @Prop() hideDefaultIcon!: boolean;
  /**
   * This is a flag indicating that the placeholder should not be cleared if readonly mode is active.
   */
  @Prop({
    default: true
  }) clearPlaceholderIfEmpty!: boolean;
  @Prop() readonly!: boolean;
  /**
   * Indicates that we should render a simple mode of this component (increases the rendering performance)
   */
  @Prop() simple!: boolean;
  /**
   * Indicates that all errors should be shown
   */
  @Prop() forceShowErrors!: boolean;
  /**
   * any external error present
   */
  @Prop() error!: boolean;
  @Prop() styleContext!: FieldStyleContext;
  showPicker = false;
  listeners: Record<string, any> = {};
  internalValue: string = '';
  validRegex = /^\#([A-Fa-f0-9]){8}$/;
  iconService = ICONS_SERVICE_INSTANCE;

  rules = [(value: string) => {
    // validates the field value
    if (value && (this.forceShowErrors || !this.readonly)) {
      value = this.fixNonAlphaValue(value);
      if (!this.validRegex.test(value)) {
        return 'ungültige Eingabe (' + this.value + ')';
      }
    } else if (!value && this.required && (this.forceShowErrors || !this.readonly)) {
      return 'Farbwert fehlt';
    }
    const notHasAnyExternalError = typeof this.error === 'boolean' ? !this.error : true;
    return notHasAnyExternalError; // if any error from outside or any local error, return false
  }];

  $refs!: {
    inputfield: Vue
  };

  private originalInputListeners: any[] = [];
  private originalChangeListeners: any[] = [];

  created() {
    if (this.value) {
      // if somebody provided a value from outside
      this.updateInternalModel(this.value);
    }

    if (this.$listeners.input) {
      if (Array.isArray(this.$listeners.input)) {
        this.originalInputListeners = this.originalInputListeners.concat(this.$listeners.input);
      } else {
        this.originalInputListeners.push(this.$listeners.input);
      }
    }
    if (this.$listeners.change) {
      if (Array.isArray(this.$listeners.change)) {
        this.originalChangeListeners = this.originalChangeListeners.concat(this.$listeners.change);
      } else {
        this.originalChangeListeners.push(this.$listeners.change);
      }
    }
    this.listeners = {
      ...this.$listeners,
      input: (newValue: string) => {
        this.applyModelValueIfValid(newValue, (valueToBePublished) => {
          // update model only if valid.
          for (const fnc of this.originalInputListeners) {
            fnc(valueToBePublished);
          }
        });
      },
      change: (newValue: string) => {
        this.applyModelValueIfValid(newValue, (valueToBePublished) => {
          // update model only if valid.
          for (const fnc of this.originalChangeListeners) {
            fnc(valueToBePublished);
          }
        });
      }
    };
  }

  @Watch('value') modelChanges(newValue: string, oldValue: string) {
    if (!isEqual(newValue, oldValue)) {
      // if somebody provided a new value from outside
      this.updateInternalModel(newValue);
      this.refreshValidation();
    }
  }

  @Watch('required') requiredChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      if (!newValue) {
        this.refreshValidation();
      }
    }
  }

  @Watch('showPicker') pickerVisible(newValue: string) {
    if (newValue) {
      this.$emit('focus');
    } else {
      this.$emit('blur');
    }
  }

  @Watch('error') errorChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      this.refreshValidation();
    }
  }

  afterColorPicked(newValue: string) {
    if (!isEqual(newValue, this.value)) {
      for (const fnc of (this.originalChangeListeners || []).concat(this.originalInputListeners || [])) {
        fnc(newValue);
      }
    }
    Vue.nextTick(() => {
      this.refreshValidation();
    });
  }

  captureEscKey(event: Event) {
    if (this.showPicker) {
      // prevent blubbling of escape event if datepicker is shown
      event.stopPropagation();
      this.showPicker = false;
    }
  }

  private updateInternalModel(newValue: string) {
    this.internalValue = (newValue || '').toUpperCase();
    this.internalValue = this.fixNonAlphaValue(this.internalValue);
  }

  private applyModelValueIfValid(newValue: string, validCb: (valueToBePublished: string | null) => void) {
    newValue = this.fixNonAlphaValue(newValue);
    if (this.validRegex.test(newValue)) {
      validCb(newValue.toUpperCase());
    } else if (newValue === '') {
      validCb('');
    }
  }

  private refreshValidation() {
    if (!this.simple) {
      const inputFieldRef = this.$refs.inputfield as any;
      if (inputFieldRef !== undefined) {
        inputFieldRef.validate();
      } else {
        throw new Error('Cannot refresh validation, the input field is not available');
      }
    }
  }

  /**
   * If the input value conforms to rgb, but not the expected default rgba, <br>
   * this method appends the alpha channel to FF. <br>
   * (The color picker does this with every color anyway, <br>
   * and none-rgba colors are supposed to have a 100% alpha channel anyway
   */
  private fixNonAlphaValue(value: string): string {
    if (value && value.length === 7) {
      value = value + 'FF';
    }
    return value;
  }

}
